import apiClient from './client'
import type {
  Feed,
  CreateFeedRequest,
  UpdateFeedRequest,
  FeedExecution,
  OpmlImportResult,
  ApiResponse,
  PaginatedResponse,
} from '@/types/models'
import {
  adaptListResponse,
  adaptFeedResponse,
  adaptFeedRequest,
  adaptOpmlImportResponse,
} from '@/lib/utils/api-adapter'

/**
 * Feed API endpoints
 */
export const feedsApi = {
  /**
   * List all feeds for the current user
   */
  async list(params?: {
    limit?: number
    offset?: number
    enabled?: boolean
    /** Keep only feeds carrying this tag slug. Unknown/empty slug is a 400. */
    tag?: string
  }): Promise<PaginatedResponse<Feed>> {
    const response = await apiClient.get<any>('/api/v1/feeds', { params })
    const adapted = adaptListResponse<any>(response, 'feeds')
    return {
      ...adapted,
      data: adapted.data.map(adaptFeedResponse),
    }
  },

  /**
   * Get a single feed by ID
   */
  async get(id: string): Promise<ApiResponse<Feed>> {
    const response = await apiClient.get<any>(`/api/v1/feeds/${id}`)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Create a new feed
   */
  async create(data: CreateFeedRequest): Promise<ApiResponse<Feed>> {
    const apiRequest = adaptFeedRequest(data)
    const response = await apiClient.post<any>('/api/v1/feeds', apiRequest)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Update an existing feed
   */
  async update(
    id: string,
    data: UpdateFeedRequest
  ): Promise<ApiResponse<Feed>> {
    const apiRequest = adaptFeedRequest(data)
    const response = await apiClient.put<any>(`/api/v1/feeds/${id}`, apiRequest)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Delete a feed
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/v1/feeds/${id}`)
  },


  /**
   * Get feed execution history
   */
  async getExecutions(
    id: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<FeedExecution>> {
    return apiClient.get<PaginatedResponse<FeedExecution>>(
      `/api/v1/feeds/${id}/executions`,
      { params }
    )
  },

  /**
   * Enable a feed
   */
  async enable(id: string): Promise<ApiResponse<Feed>> {
    const response = await apiClient.post<any>(`/api/v1/feeds/${id}/enable`)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Disable a feed
   */
  async disable(id: string): Promise<ApiResponse<Feed>> {
    const response = await apiClient.post<any>(`/api/v1/feeds/${id}/disable`)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Trigger immediate feed fetch (bypassing schedule)
   */
  async fetchNow(id: string): Promise<ApiResponse<{ message: string; feed_id: string }>> {
    return apiClient.post(`/api/v1/feeds/${id}/fetch`)
  },

  /**
   * Set temporary refresh interval override
   */
  async setOverride(
    id: string,
    data: { refresh_interval: string; duration_minutes: number }
  ): Promise<ApiResponse<{ message: string; refresh_interval: string; expires_at: string }>> {
    return apiClient.post(`/api/v1/feeds/${id}/override`, data)
  },

  /**
   * Clear refresh interval override
   */
  async clearOverride(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete(`/api/v1/feeds/${id}/override`)
  },

  /**
   * Replace a feed's tag set. Unknown names are created (name -> slug);
   * an empty array clears every tag.
   */
  async setTags(id: string, tags: string[]): Promise<ApiResponse<Feed>> {
    const response = await apiClient.put<any>(`/api/v1/feeds/${id}/tags`, { tags })
    return { data: adaptFeedResponse(response) }
  },

  /**
   * Add and/or remove tags across many feeds in one call. `matched` counts
   * the requested ids that are live feeds of the caller, not rows changed.
   */
  async bulkTags(data: {
    feedIds: string[]
    add?: string[]
    remove?: string[]
  }): Promise<{ matched: number }> {
    const body: Record<string, unknown> = { feed_ids: data.feedIds }
    if (data.add?.length) body.add = data.add
    if (data.remove?.length) body.remove = data.remove
    const response = await apiClient.post<any>('/api/v1/feeds/tags', body)
    return { matched: response?.matched ?? 0 }
  },

  /**
   * OPML 2.0 document of every live feed, tags in the `category` attribute.
   */
  async exportOpml(): Promise<{ blob: Blob; filename: string }> {
    const { blob, filename } = await apiClient.getBlob('/api/v1/feeds/export.opml')
    return { blob, filename: filename ?? 'rivulet-feeds.opml' }
  },

  /**
   * Subscribe to every feed in an OPML document. `dryRun` previews without
   * writing (created entries then have an empty id). Over 5 MiB or more than
   * 2000 outlines is a 413.
   */
  async importOpml(xml: string, dryRun: boolean): Promise<OpmlImportResult> {
    const response = await apiClient.postRaw<any>(
      '/api/v1/feeds/import.opml',
      xml,
      'application/xml',
      { params: { dry_run: dryRun } }
    )
    return adaptOpmlImportResponse(response)
  },
}

export default feedsApi
